import React, {useState} from 'react'
import {aiInterpApi} from './api'
import type {PendingFeedbackDto, SubmitFeedbackRequest} from './types'

const SCALE: {value: number; label: string}[] = [
  {value: 1, label: 'Unusable'},
  {value: 2, label: 'Poor'},
  {value: 3, label: 'Acceptable'},
  {value: 4, label: 'Good'},
  {value: 5, label: 'Excellent'},
]

type RatingField =
  | 'ratingAccuracy' | 'ratingCompleteness' | 'ratingNaturalness'
  | 'ratingSpeed' | 'ratingClarity' | 'ratingFlow'
  | 'ratingEaseOfUse' | 'ratingTrust'

const GROUPS: {section: string; items: {field: RatingField; label: string}[]}[] = [
  {section: 'Interpretation', items: [
    {field: 'ratingAccuracy',     label: 'Accuracy'},
    {field: 'ratingCompleteness', label: 'Completeness'},
    {field: 'ratingNaturalness',  label: 'Naturalness'},
  ]},
  {section: 'Delivery', items: [
    {field: 'ratingSpeed',   label: 'Speed'},
    {field: 'ratingClarity', label: 'Clarity'},
    {field: 'ratingFlow',    label: 'Flow'},
  ]},
  {section: 'Experience', items: [
    {field: 'ratingEaseOfUse', label: 'Ease of use'},
    {field: 'ratingTrust',     label: 'Trust'},
  ]},
]

const TESTED_BOTH_OPTIONS = ['Yes', 'No']
const OVERALL_PREFERENCE_OPTIONS = ['AI', 'Human', 'No Preference']
const COULD_AI_REPLACE_OPTIONS = ['Yes', 'Yes, for simple conversations only', 'No']

const TEXT_MAX_LENGTH = 1000
const COMMENT_MAX_LENGTH = 2000

interface Props { pending: PendingFeedbackDto; onSubmitted: () => void }

interface ExtraFields {
  testedBothInterpretations: string
  overallPreference: string
  aiBetterAt: string
  humanBetterAt: string
  couldAiReplaceHuman: string
  comment: string
  biggestIssue: string
  whatWorkedWell: string
  oneThingToImprove: string
}

const EMPTY_EXTRA: ExtraFields = {
  testedBothInterpretations: '',
  overallPreference: '',
  aiBetterAt: '',
  humanBetterAt: '',
  couldAiReplaceHuman: '',
  comment: '',
  biggestIssue: '',
  whatWorkedWell: '',
  oneThingToImprove: '',
}

const nullIfBlank = (v: string): string | null => {
  const t = v.trim()
  return t.length > 0 ? t : null
}

const RequiredMark: React.FC = () => <span className='text-danger'> *</span>

const TextField: React.FC<{
  id: string; label: string; required?: boolean
  value: string; maxLength: number; onChange: (v: string) => void
}> = ({id, label, required, value, maxLength, onChange}) => (
  <div className='mb-3'>
    <label htmlFor={id} className='fw-semibold mb-1 d-block'>
      {label}{required && <RequiredMark />}
    </label>
    <input
      id={id}
      type='text'
      className='form-control'
      maxLength={maxLength}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  </div>
)

const TextAreaField: React.FC<{
  id: string; label: string; required?: boolean
  value: string; maxLength: number; placeholder?: string
  onChange: (v: string) => void
}> = ({id, label, required, value, maxLength, placeholder, onChange}) => (
  <div className='mb-3'>
    <label htmlFor={id} className='fw-semibold mb-1 d-block'>
      {label}{required && <RequiredMark />}
    </label>
    <textarea
      id={id}
      className='form-control'
      rows={3}
      maxLength={maxLength}
      value={value}
      placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
    />
    <div className='form-text text-end'>{value.length} / {maxLength}</div>
  </div>
)

const RadioField: React.FC<{
  name: string; label: string; options: string[]
  value: string; onChange: (v: string) => void
}> = ({name, label, options, value, onChange}) => (
  <div className='mb-3'>
    <div className='fw-semibold mb-1'>{label}<RequiredMark /></div>
    <div className='d-flex gap-3 flex-wrap'>
      {options.map(opt => (
        <label key={opt} className='form-check'>
          <input
            type='radio'
            className='form-check-input me-1'
            name={name}
            value={opt}
            checked={value === opt}
            onChange={() => onChange(opt)}
          />
          {opt}
        </label>
      ))}
    </div>
  </div>
)

export const AIFeedbackForm: React.FC<Props> = ({pending, onSubmitted}) => {
  const [values, setValues] = useState<Partial<Record<RatingField, number>>>({})
  const [extra, setExtra] = useState<ExtraFields>(EMPTY_EXTRA)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const allRated = GROUPS.every(g => g.items.every(i => values[i.field] !== undefined))
  const requiredFilled =
    extra.testedBothInterpretations !== '' &&
    extra.overallPreference !== '' &&
    extra.aiBetterAt.trim() !== '' &&
    extra.humanBetterAt.trim() !== '' &&
    extra.couldAiReplaceHuman !== ''
  const canSubmit = allRated && requiredFilled

  const setField = (key: keyof ExtraFields, value: string) =>
    setExtra(e => ({...e, [key]: value}))

  const submit = async () => {
    if (!canSubmit) return
    setBusy(true); setError(null)
    try {
      const payload: SubmitFeedbackRequest = {
        submissionId: pending.submissionId,
        ratingAccuracy: values.ratingAccuracy!,
        ratingCompleteness: values.ratingCompleteness!,
        ratingNaturalness: values.ratingNaturalness!,
        ratingSpeed: values.ratingSpeed!,
        ratingClarity: values.ratingClarity!,
        ratingFlow: values.ratingFlow!,
        ratingEaseOfUse: values.ratingEaseOfUse!,
        ratingTrust: values.ratingTrust!,
        testedBothInterpretations: extra.testedBothInterpretations,
        overallPreference: extra.overallPreference,
        aiBetterAt: extra.aiBetterAt.trim(),
        humanBetterAt: extra.humanBetterAt.trim(),
        couldAiReplaceHuman: extra.couldAiReplaceHuman,
        comment: nullIfBlank(extra.comment),
        biggestIssue: nullIfBlank(extra.biggestIssue),
        whatWorkedWell: nullIfBlank(extra.whatWorkedWell),
        oneThingToImprove: nullIfBlank(extra.oneThingToImprove),
      }
      await aiInterpApi.submit(payload)
      onSubmitted()
    } catch (e: any) { setError(String(e?.message ?? e)) }
    finally { setBusy(false) }
  }

  return (
    <div>
      <div className='mb-3'>
        <div><strong>Interpreter:</strong> {pending.interpreterName ?? '—'}</div>
        <div><strong>Time:</strong> {new Date(pending.callStartedAtUtc).toLocaleString()}</div>
        <div><strong>Language Pair:</strong> {pending.languageA} → {pending.languageB}</div>
        <div><strong>Duration:</strong> {pending.durationSeconds ?? 0}s</div>
        <div><strong>Service Type:</strong> {pending.serviceType}</div>
      </div>

      {GROUPS.map(g => (
        <fieldset key={g.section} className='mb-3'>
          <legend className='fs-5'>{g.section}</legend>
          {g.items.map(it => (
            <div key={it.field} className='mb-2'>
              <div className='fw-semibold'>{it.label}</div>
              <div className='d-flex gap-3 flex-wrap'>
                {SCALE.map(s => (
                  <label key={s.value} className='form-check'>
                    <input
                      type='radio'
                      className='form-check-input me-1'
                      name={String(it.field)}
                      aria-label={`${it.label} — ${s.label}`}
                      checked={values[it.field] === s.value}
                      onChange={() => setValues(v => ({...v, [it.field]: s.value}))}
                    />
                    {s.value} — {s.label}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </fieldset>
      ))}

      <fieldset className='mb-3'>
        <legend className='fs-5'>Comparison</legend>
        <RadioField
          name='testedBothInterpretations'
          label='Did you test both AI and human interpretation calls?'
          options={TESTED_BOTH_OPTIONS}
          value={extra.testedBothInterpretations}
          onChange={v => setField('testedBothInterpretations', v)}
        />
        <RadioField
          name='overallPreference'
          label='Overall Preference'
          options={OVERALL_PREFERENCE_OPTIONS}
          value={extra.overallPreference}
          onChange={v => setField('overallPreference', v)}
        />
        <TextField
          id='ai-fb-ai-better'
          label='AI was better at'
          required
          value={extra.aiBetterAt}
          maxLength={TEXT_MAX_LENGTH}
          onChange={v => setField('aiBetterAt', v)}
        />
        <TextField
          id='ai-fb-human-better'
          label='Human was better at'
          required
          value={extra.humanBetterAt}
          maxLength={TEXT_MAX_LENGTH}
          onChange={v => setField('humanBetterAt', v)}
        />
        <RadioField
          name='couldAiReplaceHuman'
          label='Could AI replace a human interpreter here?'
          options={COULD_AI_REPLACE_OPTIONS}
          value={extra.couldAiReplaceHuman}
          onChange={v => setField('couldAiReplaceHuman', v)}
        />
      </fieldset>

      <fieldset className='mb-3'>
        <legend className='fs-5'>Additional feedback</legend>
        <TextAreaField
          id='ai-feedback-comment'
          label='Please provide any additional feedback or suggestions'
          value={extra.comment}
          maxLength={COMMENT_MAX_LENGTH}
          placeholder='Anything else you would like to share about this session?'
          onChange={v => setField('comment', v)}
        />
        <TextAreaField
          id='ai-fb-biggest-issue'
          label='What was the biggest issue during the call?'
          value={extra.biggestIssue}
          maxLength={COMMENT_MAX_LENGTH}
          onChange={v => setField('biggestIssue', v)}
        />
        <TextAreaField
          id='ai-fb-worked-well'
          label='What worked well?'
          value={extra.whatWorkedWell}
          maxLength={COMMENT_MAX_LENGTH}
          onChange={v => setField('whatWorkedWell', v)}
        />
        <TextAreaField
          id='ai-fb-improve'
          label='What is one thing which could be improved?'
          value={extra.oneThingToImprove}
          maxLength={COMMENT_MAX_LENGTH}
          onChange={v => setField('oneThingToImprove', v)}
        />
      </fieldset>

      {error && <div className='text-danger mb-2' role='alert'>{error}</div>}
      <button className='btn btn-primary' disabled={!canSubmit || busy} onClick={submit}>
        {busy ? 'Submitting…' : 'Submit'}
      </button>
    </div>
  )
}
